import React, { useContext, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { AuthContext } from "../../../context/authcontext";
import {
  useFilterOptions,
  UseFilterOptionsFlags,
} from "../../../hooks/useFilterOptions";
import { languageOptions } from "../../../components/language";
import { currencyOptions } from "../../../components/currency";
import { timezoneOptions } from "../../../components/timezone";
import FilterChips, {
  FilterChipItem,
} from "../../../components/filters/FilterChips";
import { FilterState } from "../../../components/filters/TableFilters";
import { getLifecycleTypeName } from "../../../utils/lifecycleUtils";
import {
  fetchStatusOptions,
  LifecycleEntityType,
  STATUS_FIELD_LABEL,
} from "./LifecycleFilterDropdown";

export type ListEntityType = LifecycleEntityType | "company" | "contact";

type OptionSource = keyof UseFilterOptionsFlags;

interface Option {
  value: string | number;
  label: string;
}

interface FieldSpec {
  label: string | (() => string);
  icon: string;
  options?: OptionSource | Option[];
  format?: (value: any) => string;
}

const formatDate = (value: any) => {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : String(value);
};

const FIELD_SPECS: Record<string, FieldSpec> = {
  priority: { label: "Priority", icon: "ti ti-flag" },
  company_id: { label: "Company", icon: "ti ti-building", options: "companies" },
  assigned_team_id: {
    label: "Assigned Team",
    icon: "ti ti-users-group",
    options: "teams",
  },
  teamFilter: { label: "Assigned Team", icon: "ti ti-users-group", options: "teams" },
  assigned_user_id: { label: "Assigned User", icon: "ti ti-user", options: "users" },
  userFilter: { label: "Assigned User", icon: "ti ti-user", options: "users" },
  lifecycle_source_id: {
    label: "Source",
    icon: "ti ti-world",
    options: "lifecycleSources",
  },
  sourceFilter: { label: "Source", icon: "ti ti-world" },
  engagement_source: { label: "Engagement Source", icon: "ti ti-world" },
  related_lead_id: {
    label: () => `Related ${getLifecycleTypeName("LEAD")}`,
    icon: "ti ti-link",
    options: "leads",
  },
  related_pipeline_id: {
    label: () => `Related ${getLifecycleTypeName("PIPELINE")}`,
    icon: "ti ti-link",
    options: "pipelines",
  },
  related_deal_id: {
    label: () => `Related ${getLifecycleTypeName("DEAL")}`,
    icon: "ti ti-link",
    options: "deals",
  },
  related_proposal_id: {
    label: () => `Related ${getLifecycleTypeName("PROPOSAL")}`,
    icon: "ti ti-link",
    options: "proposals",
  },
  industry_type_id: {
    label: "Industry Type",
    icon: "ti ti-briefcase",
    options: "industryTypes",
  },
  business_type_id: {
    label: "Business Type",
    icon: "ti ti-building-store",
    options: "businessTypes",
  },
  is_active: {
    label: "Active",
    icon: "ti ti-circle-check",
    format: (value) => (String(value) === "true" ? "Active" : "Inactive"),
  },
  currency_code: { label: "Currency", icon: "ti ti-coin", options: currencyOptions },
  time_zone: { label: "Timezone", icon: "ti ti-clock", options: timezoneOptions },
  preferred_language: {
    label: "Language",
    icon: "ti ti-language",
    options: languageOptions,
  },
  start_datetime: {
    label: "Start Date",
    icon: "ti ti-calendar-event",
    format: formatDate,
  },
  due_datetime: { label: "Due Date", icon: "ti ti-calendar-event", format: formatDate },
  expiration_datetime: {
    label: "Expiration Date",
    icon: "ti ti-calendar-event",
    format: formatDate,
  },
  phone: { label: "Phone", icon: "ti ti-phone" },
  email: { label: "Email", icon: "ti ti-mail" },
  tags: { label: "Tags", icon: "ti ti-tag" },
  country: { label: "Country", icon: "ti ti-map-pin" },
  state: { label: "State", icon: "ti ti-map-pin" },
  city: { label: "City", icon: "ti ti-map-pin" },
  address: { label: "Address", icon: "ti ti-map-pin" },
  website: { label: "Website", icon: "ti ti-world-www" },
  annual_revenue: { label: "Annual Revenue", icon: "ti ti-currency-dollar" },
};

const NON_FIELD_KEYS = [
  "searchTerm",
  "dateRange",
  "statusFilter",
  "archivedFilter",
  "is_archive",
  "sortBy",
  "sortOrder",
  "sort_by",
  "industryFilter",
  "countryFilter",
];

const NULL_ON_REMOVE_KEYS = ["teamFilter", "userFilter", "sourceFilter"];

const isFilled = (value: any) =>
  value !== undefined &&
  value !== null &&
  value !== "" &&
  !(Array.isArray(value) && value.length === 0);

const humanize = (key: string) =>
  key
    .replace(/_id$/, "")
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const getClearedListFilters = (): FilterState => ({
  searchTerm: "",
  dateRange: [null, null],
  industryFilter: [],
  countryFilter: [],
  statusFilter: null,
  sortBy: "created_datetime",
  sortOrder: "desc",
  archivedFilter: "no",
});

interface ListFilterChipsProps {
  entityType: ListEntityType;
  filters: Record<string, any>;
  onRemove: (patch: Record<string, any>) => void;
  onClearAll: () => void;
  disabled?: boolean;
  className?: string;
}

const ListFilterChips: React.FC<ListFilterChipsProps> = ({
  entityType,
  filters,
  onRemove,
  onClearAll,
  disabled = false,
  className = "mb-0",
}) => {
  const { selectedTenantId } = useContext(AuthContext);
  const [statusOptions, setStatusOptions] = useState<Option[]>([]);

  const fieldKeys = useMemo(
    () =>
      Object.keys(filters).filter(
        (key) => !NON_FIELD_KEYS.includes(key) && isFilled(filters[key])
      ),
    [filters]
  );

  const optionFlags = useMemo(() => {
    const flags: UseFilterOptionsFlags = {};
    fieldKeys.forEach((key) => {
      const source = FIELD_SPECS[key]?.options;
      if (typeof source === "string") flags[source] = true;
    });
    return flags;
  }, [fieldKeys]);

  const loadedOptions = useFilterOptions(
    selectedTenantId,
    optionFlags,
    Object.keys(optionFlags).length > 0
  );

  const hasStatusFilter =
    entityType !== "company" &&
    entityType !== "contact" &&
    isFilled(filters.statusFilter);

  useEffect(() => {
    if (!hasStatusFilter || !selectedTenantId || statusOptions.length) return;
    let cancelled = false;
    fetchStatusOptions(
      entityType as LifecycleEntityType,
      parseInt(String(selectedTenantId), 10)
    )
      .then((options) => {
        if (!cancelled) {
          setStatusOptions(options.map((o) => ({ value: o.id, label: o.label })));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [hasStatusFilter, selectedTenantId, entityType, statusOptions.length]);

  const labelFrom = (options: Option[] | undefined, value: any) =>
    options?.find((option) => String(option.value) === String(value))?.label ??
    String(value);

  const chips: FilterChipItem[] = [];

  if (isFilled(filters.searchTerm)) {
    chips.push({
      key: "search",
      icon: "ti ti-search",
      label: "Search",
      value: String(filters.searchTerm),
      onRemove: () => onRemove({ searchTerm: "" }),
    });
  }

  const [startDate, endDate] = Array.isArray(filters.dateRange)
    ? filters.dateRange
    : [null, null];
  if (startDate || endDate) {
    chips.push({
      key: "date",
      icon: "ti ti-calendar",
      label: "Created",
      value: [startDate, endDate]
        .map((date) => (date ? dayjs(date).format("DD MMM YYYY") : "…"))
        .join(" – "),
      onRemove: () => onRemove({ dateRange: [null, null] }),
    });
  }

  if (filters.sort_by) {
    chips.push({
      key: "sort",
      icon: "ti ti-sort-ascending-2",
      label: "Sort",
      value: filters.sort_by === "oldest" ? "Oldest" : "Newest",
      onRemove: () =>
        onRemove({
          sort_by: undefined,
          sortBy: "created_datetime",
          sortOrder: "desc",
        }),
    });
  }

  if (hasStatusFilter) {
    chips.push({
      key: "status",
      icon: "ti ti-progress-check",
      label: STATUS_FIELD_LABEL[entityType as LifecycleEntityType],
      value: labelFrom(statusOptions, filters.statusFilter),
      onRemove: () => onRemove({ statusFilter: null }),
    });
  }

  if (filters.archivedFilter && filters.archivedFilter !== "no") {
    chips.push({
      key: "archived",
      icon: "ti ti-archive",
      label: "Archived",
      value: filters.archivedFilter === "yes" ? "Yes" : "Both",
      onRemove: () => onRemove({ archivedFilter: "no" }),
    });
  }

  const orderedFieldKeys = [
    ...Object.keys(FIELD_SPECS).filter((key) => fieldKeys.includes(key)),
    ...fieldKeys.filter((key) => !FIELD_SPECS[key]),
  ];

  orderedFieldKeys.forEach((key) => {
    const spec = FIELD_SPECS[key];
    const value = filters[key];
    const options =
      typeof spec?.options === "string"
        ? (loadedOptions[spec.options] as Option[])
        : spec?.options;
    const displayValue = spec?.format
      ? spec.format(value)
      : Array.isArray(value)
        ? value.map((item) => labelFrom(options, item)).join(", ")
        : labelFrom(options, value);

    chips.push({
      key,
      icon: spec?.icon || "ti ti-filter",
      label: spec
        ? typeof spec.label === "function"
          ? spec.label()
          : spec.label
        : humanize(key),
      value: displayValue,
      onRemove: () =>
        onRemove({ [key]: NULL_ON_REMOVE_KEYS.includes(key) ? null : undefined }),
    });
  });

  return (
    <FilterChips
      chips={chips}
      onClearAll={onClearAll}
      disabled={disabled}
      className={className}
    />
  );
};

export default ListFilterChips;
